import { useEffect } from 'react';

import type { StaffLessonNow } from '@/types/staff';

/** Past the bell, so the server's clock has certainly moved on too. */
const AFTER_BELL_MS = 5_000;

interface WithLessons {
  nowTeaching?: StaffLessonNow | null;
  nextClass?: StaffLessonNow | null;
}

/** Minutes since midnight at the school, with seconds as a fraction. IST, never the browser's zone. */
function schoolClockMinutes(): number {
  const [hours, minutes, seconds] = new Date()
    .toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false })
    .split(':')
    .map(Number);
  return hours * 60 + minutes + seconds / 60;
}

function toMinutes(clock: string): number {
  const [hours, minutes] = clock.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Re-reads the list when the next bell rings, so a "Now / Next" column moves
 * on with the day instead of showing the period the page was opened in.
 *
 * The server works out now and next (`readStaffNowAndNext`); this only knows
 * when that answer stops being true — the earliest end of a lesson in progress
 * or start of one to come, across the rows on screen. One timer, set afresh
 * each time the rows change, so nothing polls while no lesson is left today.
 */
export function useRefetchAtNextBell(
  rows: readonly WithLessons[] | undefined,
  refetch: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled || !rows) return;

    const clock = schoolClockMinutes();
    const bells = rows
      .flatMap((row) => [row.nowTeaching?.endTime, row.nextClass?.startTime])
      .filter((time): time is string => Boolean(time))
      .map(toMinutes)
      .filter((minutes) => minutes > clock);
    if (bells.length === 0) return;

    const wait = (Math.min(...bells) - clock) * 60_000 + AFTER_BELL_MS;
    const timer = window.setTimeout(refetch, wait);
    return () => window.clearTimeout(timer);
  }, [rows, refetch, enabled]);
}
